import { LoadingSkeleton } from '@bloomfieldrobotics/canopy';
import {
  AccordionDetails,
  Stack,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useBlocksByRanch } from '../../../services/entities/hooks/useBlocksByRanch';
import { useRanchesByCustomer } from '../../../services/entities/hooks/useRanchesByCustomer';
import { useUpdateBlockMutation } from '../../../services/entities/impl/blfApi/endpoints/blocks';
import { useFeatureFlags } from '../../../services/featureFlags/hooks/useFeatureFlags';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { CustomerDropdown } from '../../commonUI/dropdowns/CustomerDropdown';
import { ExpandableMenu } from '../../commonUI/Menu/ExpandableMenu';
import { MenuPaper } from '../../commonUI/Menu/MenuPaper';
import { SettingsTableCell } from '../components/SettingsTableCell';
import { SettingsHeaderCell } from '../components/SettingsTableHeaderCell';
import { BlockTableRow } from './components/BlockTableRow';

type BlockSettingsProps = {
  customerId: string | null;
  onCustomerChange: (customerId: string) => void;
};
export const BlockSettings = ({
  customerId,
  onCustomerChange,
}: BlockSettingsProps) => {
  const { t } = useTranslation();

  const setCustomer = (id: string) => {
    onCustomerChange(id);
    setExpandedRanch('');
  };

  const [expandedRanch, setExpandedRanch] = useState('');

  const handleExpandedChange = (ranchId: string) => {
    return (_event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpandedRanch(newExpanded ? ranchId : '');
    };
  };

  const { ranches } = useRanchesByCustomer(customerId || undefined);

  const { blocks, isBlocksLoading } = useBlocksByRanch(expandedRanch);

  const [updateBlock] = useUpdateBlockMutation();
  const handleVarietyChange = useCallback(
    (blockId: string, varietyId: string) => {
      updateBlock({
        id: blockId,
        varietyID: varietyId,
      });
    },
    [updateBlock]
  );

  const ranchList = ranches?.map((ranch) => {
    return (
      <ExpandableMenu
        title={ranch.name}
        key={ranch.id}
        expanded={ranch.id === expandedRanch}
        onChange={handleExpandedChange(ranch.id)}
      >
        <AccordionDetails>
          <Table>
            <TableHead>
              <TableRow>
                <SettingsHeaderCell sx={{ width: '20%' }}>
                  {t('Block')}
                </SettingsHeaderCell>
                <SettingsHeaderCell>{t('Variety')}</SettingsHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!blocks ? (
                <TableRow>
                  <SettingsTableCell>
                    <LoadingSkeleton loading={isBlocksLoading} height={43} />
                  </SettingsTableCell>
                  <SettingsTableCell>
                    <LoadingSkeleton loading={isBlocksLoading} height={43} />
                  </SettingsTableCell>
                </TableRow>
              ) : (
                blocks.map((block) => {
                  return (
                    <BlockTableRow
                      key={block.id}
                      block={block}
                      customerId={customerId || ''}
                      onVarietyChange={handleVarietyChange}
                    />
                  );
                })
              )}
            </TableBody>
          </Table>
        </AccordionDetails>
      </ExpandableMenu>
    );
  });

  const hasRanches = ranches && ranches.length > 0;
  return (
    <Stack spacing={8}>
      <CustomerDropdown value={customerId || ''} onChange={setCustomer} />
      <MenuPaper>
        {hasRanches ? (
          ranchList
        ) : (
          <NoRanchesFound customerId={customerId || ''} />
        )}
      </MenuPaper>
    </Stack>
  );
};

const NoRanchesFound = ({ customerId }: { customerId: string }) => {
  const { t } = useTranslation();
  const { Diagnostics } = useFeatureFlags();

  const emptyMessage = customerId
    ? Diagnostics
      ? t('No ranches found for the specified customer, ID: {{customerId}}', {
          customerId,
        })
      : t('No ranches found for the specified customer')
    : t('Please select a customer');
  return <Stack sx={{ alignItems: 'center' }}>{emptyMessage}</Stack>;
};
